import { useForm } from "react-hook-form";
import { HiArrowLeft, HiMail, HiPaperAirplane } from "react-icons/hi";
import { Link } from "react-router-dom";

import { ForgotPasswordData } from "@/types/auth";

interface ForgotPasswordFormProps {
  error?: string;
  isLoading?: boolean;
  onSubmit: (data: ForgotPasswordData) => void;
}

export default function ForgotPasswordForm({
  error,
  isLoading = false,
  onSubmit,
}: ForgotPasswordFormProps) {
  const {
    formState: { errors },
    handleSubmit,
    register,
  } = useForm<ForgotPasswordData>();

  const handleFormSubmit = (data: ForgotPasswordData) => {
    onSubmit(data);
  };

  return (
    <div className="card w-full max-w-md bg-base-100 shadow-xl">
      <div className="card-body">
        {/* Header */}
        <div className="mb-6 text-center">
          <div className="mb-4 flex justify-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-info">
              <HiPaperAirplane className="h-8 w-8 text-info-content" />
            </div>
          </div>
          <h2 className="text-2xl font-bold text-base-content">Send OTP</h2>
          <p className="mt-2 text-base-content/70">
            Enter your email address and we will send you a 6-digit verification
            code.
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="alert alert-error mb-4">
            <span className="text-sm">{error}</span>
          </div>
        )}

        {/* Form */}
        <form className="space-y-6" onSubmit={handleSubmit(handleFormSubmit)}>
          {/* Email Field */}
          <div className="form-control">
            <label className="label">
              <span className="label-text">Email Address</span>
            </label>
            <div className="relative">
              <input
                {...register("email", {
                  pattern: {
                    message: "Invalid email address",
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  },
                  required: "Email is required",
                })}
                className={`input input-bordered w-full pl-10 ${
                  errors.email ? "input-error" : ""
                }`}
                disabled={isLoading}
                placeholder="Enter your email address"
                type="email"
              />
              <HiMail className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 transform text-base-content/50" />
            </div>
            {errors.email && (
              <label className="label">
                <span className="label-text-alt text-error">
                  {errors.email.message}
                </span>
              </label>
            )}
          </div>

          {/* Submit Button */}
          <button
            className="btn btn-primary w-full"
            disabled={isLoading}
            type="submit"
          >
            {isLoading ? (
              <>
                <span className="loading loading-spinner loading-sm"></span>
                Sending OTP...
              </>
            ) : (
              <>
                <HiPaperAirplane className="mr-2 h-4 w-4" />
                Send OTP
              </>
            )}
          </button>
        </form>

        {/* Back to Login */}
        <div className="mt-6 text-center">
          <Link className="btn btn-ghost btn-sm normal-case" to="/auth/login">
            <HiArrowLeft className="mr-2 h-4 w-4" />
            Back to Login
          </Link>
        </div>

        {/* Additional Info */}
        <div className="mt-6 rounded-lg bg-base-200 p-4">
          <h4 className="mb-2 text-sm font-semibold">What happens next?</h4>
          <ul className="space-y-1 text-xs text-base-content/70">
            <li>• We will send a 6-digit code to your email</li>
            <li>• Enter the code on the next screen</li>
            <li>• Create a new secure password</li>
            <li>• The code expires in 5 minutes for security</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
